/**
 * A escala dos controles deslizantes da bancada de cálculo.
 *
 * Puro, com prova própria (`app/tests/bancada.mjs`).
 *
 * ---------------------------------------------------------------------------
 * POR QUE NÃO UMA ESCALA LINEAR
 * ---------------------------------------------------------------------------
 * A receita bruta do Simples vai de zero a R$ 4.800.000. Num controle linear,
 * a primeira faixa (até R$ 180 mil) ocupa **3,75%** do curso: quem fatura R$ 15
 * mil por mês não consegue parar no próprio número — um pixel vale R$ 12 mil.
 * E a maior parte dos clientes de quem faz esta pós está justamente ali.
 *
 * A escala aqui é POR FAIXA: cada uma das seis faixas legais ocupa o mesmo
 * trecho do curso. Isso resolve a precisão embaixo e, de quebra, mostra o que a
 * tabela progressiva faz — arrastando, o aluno atravessa seis degraus de
 * alíquota em seis passos iguais, e vê que a última faixa (R$ 1,2 MILHÃO de
 * amplitude) cabe no mesmo espaço que a primeira (R$ 180 mil).
 *
 * A ida e a volta são inversas uma da outra, e a prova cobra isso nos dois
 * sentidos: um controle cuja posição não devolve o próprio valor "pula" sob o
 * dedo de quem arrasta, e o defeito só aparece no uso.
 */

/** As seis fronteiras de faixa da LC 123/2006 — o topo de cada uma. */
export const FRONTEIRAS_RBT12 = [180_000, 360_000, 720_000, 1_800_000, 3_600_000, 4_800_000];

/** Quantos passos o controle tem dentro de cada faixa. */
const PASSOS_POR_FAIXA = 100;

/** O curso inteiro do controle, em passos. */
export const PASSOS_TOTAIS = PASSOS_POR_FAIXA * FRONTEIRAS_RBT12.length;

/** O piso de uma faixa (o topo da anterior, ou zero na primeira). */
function pisoDaFaixa(i: number, fronteiras: number[]): number {
  return i === 0 ? 0 : fronteiras[i - 1];
}

/**
 * Posição do controle (0..PASSOS_TOTAIS) para um valor em reais.
 *
 * Valor acima do teto satura no fim — e não estoura —, porque o controle é
 * navegação: quem digitou R$ 6 milhões no campo continua vendo o controle no
 * extremo, com o campo dizendo o número de verdade. Estourar a barra faria a
 * peça mentir sobre a própria amplitude.
 */
export function posicaoDoValor(valor: number, fronteiras = FRONTEIRAS_RBT12): number {
  if (!Number.isFinite(valor) || valor <= 0) return 0;
  for (let i = 0; i < fronteiras.length; i++) {
    const piso = pisoDaFaixa(i, fronteiras);
    const teto = fronteiras[i];
    if (valor <= teto) {
      return i * PASSOS_POR_FAIXA + ((valor - piso) / (teto - piso)) * PASSOS_POR_FAIXA;
    }
  }
  return fronteiras.length * PASSOS_POR_FAIXA;
}

/** Valor em reais para uma posição do controle. A inversa da de cima. */
export function valorDaPosicao(posicao: number, fronteiras = FRONTEIRAS_RBT12): number {
  const total = fronteiras.length * PASSOS_POR_FAIXA;
  if (!Number.isFinite(posicao) || posicao <= 0) return 0;
  if (posicao >= total) return fronteiras[fronteiras.length - 1];
  // `min` no índice porque `posicao === total` já saiu acima, mas ponto
  // flutuante em `posicao / 100` pode devolver o índice de fora na borda.
  const i = Math.min(Math.floor(posicao / PASSOS_POR_FAIXA), fronteiras.length - 1);
  const piso = pisoDaFaixa(i, fronteiras);
  const teto = fronteiras[i];
  const dentro = (posicao - i * PASSOS_POR_FAIXA) / PASSOS_POR_FAIXA;
  return Math.round(piso + (teto - piso) * dentro);
}

/**
 * As marcas do controle, em porcentagem do curso — para desenhar os degraus.
 *
 * A última NÃO entra: ela cairia sobre a borda direita do trilho, onde já há a
 * ponta. Marca sobre a ponta é ruído que o olho lê como defeito de alinhamento.
 */
export function marcasDasFaixas(fronteiras = FRONTEIRAS_RBT12): { valor: number; pct: number }[] {
  return fronteiras.slice(0, -1).map((valor, i) => ({
    valor,
    pct: ((i + 1) * PASSOS_POR_FAIXA * 100) / (fronteiras.length * PASSOS_POR_FAIXA),
  }));
}

/**
 * Uma escala LINEAR com o mesmo contrato, para as grandezas que não têm faixa
 * legal (salário, folha de um mês, pró-labore).
 *
 * Existe para a bancada ter UM só tipo de controle: dois componentes diferentes
 * para "arrastar um número" divergiriam no primeiro ajuste de acessibilidade.
 */
export function escalaLinear(maximo: number) {
  const teto = maximo > 0 ? maximo : 1;
  return {
    posicaoDoValor: (v: number) =>
      !Number.isFinite(v) || v <= 0 ? 0 : Math.min((v / teto) * PASSOS_TOTAIS, PASSOS_TOTAIS),
    valorDaPosicao: (p: number) =>
      !Number.isFinite(p) || p <= 0 ? 0 : Math.round(Math.min((p / PASSOS_TOTAIS) * teto, teto)),
    marcas: [] as { valor: number; pct: number }[],
  };
}

/** A escala por faixas com o mesmo contrato da linear. */
export function escalaDeFaixas(fronteiras = FRONTEIRAS_RBT12) {
  return {
    posicaoDoValor: (v: number) => posicaoDoValor(v, fronteiras),
    valorDaPosicao: (p: number) => valorDaPosicao(p, fronteiras),
    marcas: marcasDasFaixas(fronteiras),
  };
}

export type Escala = ReturnType<typeof escalaLinear>;

/**
 * O rótulo curto de um valor em reais, para caber numa marca de controle.
 *
 * "R$ 1,8 mi" em vez de "R$ 1.800.000,00": a marca tem uns 50 pixels, e o
 * número por extenso ali vira um borrão que ninguém lê. O valor exato continua
 * no campo, que é onde se lê número.
 */
export function reaisCurto(valor: number): string {
  if (!Number.isFinite(valor)) return "—";
  if (valor >= 1_000_000) {
    const mi = valor / 1_000_000;
    // Sem casa decimal quando é redondo: "R$ 3 mi" lê melhor que "R$ 3,0 mi".
    const texto = Number.isInteger(mi) ? String(mi) : mi.toFixed(1).replace(".", ",");
    return `R$ ${texto} mi`;
  }
  if (valor >= 1_000) {
    const mil = valor / 1_000;
    const texto = Number.isInteger(mil) ? String(mil) : mil.toFixed(0);
    return `R$ ${texto} mil`;
  }
  return `R$ ${valor.toFixed(0)}`;
}
